import { meta, row, rows } from "./db";
import { keyOf, pathOf } from "./mirror";

const NO_FORUM_FALLBACK = "#8A8F98";

const iso = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const todayIso = () => iso(new Date());

const shift = (dateIso, days) => {
  const [y, m, d] = dateIso.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  return iso(dt);
};

// morning first, travel and all-day last — the same order the server sends
const BUCKET = { morning: 0, post_court: 3, all_day: 4, out_of_station: 4 };
const bucketOf = (e) => BUCKET[e.event_type] ?? 1;

const byDate = (a, b) =>
  a.date === b.date
    ? bucketOf(a) - bucketOf(b) ||
      (a.start_time || "99:99").localeCompare(b.start_time || "99:99")
    : a.date.localeCompare(b.date);

const cachedRaw = async (config) => {
  const hit = await row("kv", keyOf(config));
  return hit ? hit.data : undefined;
};

const cachedUser = async () => (await row("kv", "/auth/me"))?.data || null;

const eventList = async (p = {}) => {
  let list = await rows("events");
  if (p.start) list = list.filter((e) => e.date >= p.start);
  if (p.end) list = list.filter((e) => e.date <= p.end);
  if (p.matter_id) list = list.filter((e) => e.matter_id === p.matter_id);
  if (p.unlinked_only) list = list.filter((e) => !e.matter_id);
  return list.sort(byDate);
};

const blankCell = (date) => ({
  date,
  total: 0,
  fixed_time: 0,
  session_based: 0,
  morning: 0,
  non_court: 0,
  out_of_station: 0,
  post_court: 0,
  all_day: 0,
  urgent: 0,
  placeholders: 0,
  clashes: 0,
  session_1: 0,
  session_2: 0,
  titles: [],
  forums: {},
});

const heatmap = async (p = {}) => {
  const events = await eventList({ start: p.start, end: p.end });
  const courts = Object.fromEntries((await rows("courts")).map((c) => [c.court_id, c]));
  const noForum =
    (await cachedUser())?.preferences?.no_forum_color || NO_FORUM_FALLBACK;
  const cells = {};

  events.forEach((ev) => {
    const cell = cells[ev.date] || blankCell(ev.date);
    cells[ev.date] = cell;
    cell.total += 1;
    cell[ev.event_type] = (cell[ev.event_type] || 0) + 1;
    if (ev.urgent) cell.urgent += 1;
    if (ev.is_placeholder) cell.placeholders += 1;
    if (ev.clash) cell.clashes += 1;
    const sess = ev.derived_session || ev.session_flag;
    if (sess === "both") {
      cell.session_1 += 1;
      cell.session_2 += 1;
    } else if (sess === "session_1" || sess === "session_2") {
      cell[sess] += 1;
    }
    if (cell.titles.length < 4) cell.titles.push(ev.title);
    const court = courts[ev.court_id];
    const key = court ? court.court_id : "no_forum";
    const slice = cell.forums[key] || {
      key,
      name: court ? court.name : "No forum",
      color: (court ? court.color : ev.color) || noForum,
      count: 0,
    };
    slice.count += 1;
    cell.forums[key] = slice;
  });

  const out = Object.values(cells).map((cell) => {
    const weighted =
      cell.fixed_time * 1.5 +
      cell.session_based * 1 +
      cell.non_court * 1.25 +
      cell.morning * 0.5 +
      cell.post_court * 0.75 +
      cell.out_of_station * 1.25 +
      cell.all_day * 1.25 +
      cell.urgent * 0.5;
    const forums = Object.values(cell.forums).sort(
      (a, b) => b.count - a.count || a.name.localeCompare(b.name),
    );
    return {
      ...cell,
      forums,
      load: Math.round(weighted * 100) / 100,
      level:
        weighted === 0 ? 0 : weighted <= 1.5 ? 1 : weighted <= 3 ? 2 : weighted <= 5.5 ? 3 : 4,
      dominant_color: forums[0]?.color || null,
      full_day: Boolean(cell.all_day || cell.out_of_station),
    };
  });
  out.sort((a, b) => a.date.localeCompare(b.date));
  return { start: p.start, end: p.end, cells: out };
};

const dashboardSummary = async () => {
  const day = todayIso();
  const all = await eventList();
  const horizon = shift(day, 27);
  const todayEvents = all.filter((e) => e.date === day);
  const upcoming = all.filter((e) => e.date > day && e.date <= horizon);
  const weekEnd = shift(day, 6);
  const matters = await rows("matters");
  return {
    today: day,
    role: null,
    today_events: todayEvents,
    next_up: upcoming.slice(0, 8),
    stats: {
      today_count: todayEvents.length,
      today_fixed: todayEvents.filter((e) => e.event_type === "fixed_time").length,
      week_count: todayEvents.length + upcoming.filter((e) => e.date <= weekEnd).length,
      active_matters: matters.filter((m) => (m.status || "active") === "active").length,
      open_placeholders: all.filter((e) => !e.matter_id && e.date >= day).length,
      out_of_station_upcoming: upcoming.filter((e) => e.event_type === "out_of_station").length,
    },
  };
};

const todoList = async (p = {}) => {
  let list = await rows("todos");
  if (p.date) {
    list = list.filter(
      (t) =>
        t.date <= p.date &&
        (!t.done || t.date === p.date || (t.done_at || "").slice(0, 10) === p.date),
    );
  } else if (p.start && p.end) list = list.filter((t) => t.date >= p.start && t.date <= p.end);
  if (p.matter_id) list = list.filter((t) => t.matter_id === p.matter_id);
  if (p.open_only) list = list.filter((t) => !t.done);
  const today = todayIso();
  list.forEach((t) => {
    t.overdue = !!(t.due_date && !t.done && t.due_date < today);
  });
  return list.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      Number(a.done) - Number(b.done) ||
      (a.created_at || "").localeCompare(b.created_at || ""),
  );
};

const todoSummary = async (p = {}) => {
  const all = await rows("todos");
  const today = todayIso();
  const cells = {};
  const days = [];
  for (let d = new Date(`${p.start}T00:00:00`); ; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().slice(0, 10);
    if (iso > p.end) break;
    days.push(iso);
  }
  days.forEach((day) => {
    let items = all.filter(
      (t) =>
        t.date <= day && (!t.done || t.date === day || (t.done_at || "").slice(0, 10) === day),
    );
    if (day > today) items = items.filter((t) => t.date === day || t.due_date === day);
    if (!items.length) return;
    const cell = { date: day, total: items.length, open: 0, high_open: 0 };
    items.forEach((item) => {
      if (!item.done) {
        cell.open += 1;
        if (item.priority === "high") cell.high_open += 1;
      }
    });
    cells[day] = cell;
  });
  return { start: p.start, end: p.end, cells: Object.values(cells) };
};

const carryOver = async (p = {}) => {
  const list = await rows("todos");
  return list
    .filter((t) => !t.done && p.date && t.date < p.date)
    .sort((a, b) => b.date.localeCompare(a.date));
};

const uncategorized = async (p = {}) => {
  const day = todayIso();
  const list = (await rows("events")).filter((e) => !e.matter_id);
  const upcoming = list.filter((e) => e.date >= day).sort(byDate);
  const past = list.filter((e) => e.date < day).sort((a, b) => b.date.localeCompare(a.date));
  return {
    today: day,
    upcoming,
    past: p.include_past ? past : [],
    counts: { upcoming: upcoming.length, past: past.length },
  };
};

const matterDiary = async (matterId) => {
  const matter = await row("matters", matterId);
  if (!matter) return undefined;
  const events = (await rows("events")).filter((e) => e.matter_id === matterId).sort(byDate);
  const logs = (await rows("logs"))
    .filter((l) => l.matter_id === matterId)
    .sort((a, b) => b.date.localeCompare(a.date));
  const courts = Object.fromEntries((await rows("courts")).map((c) => [c.court_id, c.name]));
  const day = todayIso();
  const upcoming = events.filter((e) => e.date >= day);
  return {
    matter,
    court_name: courts[matter.court_id] || null,
    upcoming,
    next_hearing: upcoming[0] || null,
    past: events.filter((e) => e.date < day).reverse(),
    logs,
    role: null,
  };
};

const logList = async (p = {}) => {
  let list = await rows("logs");
  if (p.matter_id) list = list.filter((l) => l.matter_id === p.matter_id);
  if (p.date) list = list.filter((l) => l.date === p.date);
  if (p.event_id) list = list.filter((l) => l.event_id === p.event_id);
  return list.sort((a, b) => b.date.localeCompare(a.date));
};

const postItList = async (p = {}) => {
  let list = await rows("postits");
  if (p.matter_id) list = list.filter((n) => n.matter_id === p.matter_id);
  if (p.event_id) list = list.filter((n) => n.event_id === p.event_id);
  if (p.todo_id) list = list.filter((n) => n.todo_id === p.todo_id);
  const order = (await meta("postit_order")) || [];
  const rank = (n) => {
    const at = order.indexOf(n.post_it_id);
    return at === -1 ? order.length + 1 : at;
  };
  return list.sort(
    (a, b) =>
      Number(Boolean(a.expired)) - Number(Boolean(b.expired)) ||
      Number(Boolean(b.pinned)) - Number(Boolean(a.pinned)) ||
      rank(a) - rank(b),
  );
};

const stripHtml = (html) => (html || "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

const localSearch = async (q = "") => {
  const needle = String(q).trim().toLowerCase();
  if (needle.length < 2) return { query: q, groups: [], total: 0 };
  const hit = (...fields) => fields.filter(Boolean).some((f) => String(f).toLowerCase().includes(needle));
  const [matters, events, logs, todos, notes, courts] = await Promise.all([
    rows("matters"),
    rows("events"),
    rows("logs"),
    rows("todos"),
    rows("postits"),
    rows("courts"),
  ]);
  const courtName = Object.fromEntries(courts.map((c) => [c.court_id, c.name]));
  const matterLine = (id) => {
    const m = matters.find((x) => x.matter_id === id);
    return m ? [m.title, m.case_number].filter(Boolean).join(" · ") : null;
  };
  const groups = [
    {
      kind: "matters",
      label: "Matters",
      items: matters
        .filter((m) => hit(m.title, m.case_number, m.client_name, m.opponent_name, m.notes))
        .slice(0, 6)
        .map((m) => ({
          id: m.matter_id,
          title: m.title,
          sub: [m.case_number, m.client_name].filter(Boolean).join(" · ") || null,
          path: `/matters/${m.matter_id}`,
        })),
    },
    {
      kind: "events",
      label: "Diary entries",
      items: events
        .filter((e) => hit(e.title, e.notes, e.location))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 6)
        .map((e) => ({
          id: e.event_id,
          title: e.title,
          sub:
            [e.date, e.start_time, courtName[e.court_id], matterLine(e.matter_id)]
              .filter(Boolean)
              .join(" · ") || null,
          path: `/calendar?date=${e.date}&view=day`,
        })),
    },
    {
      kind: "logs",
      label: "Hearing logs",
      items: logs
        .filter((l) => hit(stripHtml(l.body_html), l.summary))
        .slice(0, 6)
        .map((l) => ({
          id: l.log_id,
          title: (l.summary || stripHtml(l.body_html) || "Hearing log").slice(0, 90),
          sub: [l.date, l.kind, matterLine(l.matter_id)].filter(Boolean).join(" · ") || null,
          path: l.matter_id ? `/matters/${l.matter_id}` : "/matters",
        })),
    },
    {
      kind: "todos",
      label: "To-dos",
      items: todos
        .filter((t) => hit(t.title, t.notes))
        .slice(0, 6)
        .map((t) => ({
          id: t.todo_id,
          title: t.title,
          sub:
            [t.date, t.done ? "done" : null, matterLine(t.matter_id)].filter(Boolean).join(" · ") ||
            null,
          path: `/todos?date=${t.date}`,
        })),
    },
    {
      kind: "postits",
      label: "Post-its",
      items: notes
        .filter((n) => hit(n.title, n.body))
        .slice(0, 6)
        .map((n) => ({
          id: n.post_it_id,
          title: (n.title || n.body || "").slice(0, 90),
          sub:
            [n.priority !== "normal" ? n.priority : null, matterLine(n.matter_id)]
              .filter(Boolean)
              .join(" · ") || null,
          path: "/postits",
        })),
    },
  ].filter((g) => g.items.length);
  return { query: q, groups, total: groups.reduce((n, g) => n + g.items.length, 0) };
};

/** Answers a GET from the offline mirror. Returns undefined when nothing is cached. */
export const resolveGet = async (config) => {
  const path = pathOf(config);
  const p = { ...(config?.params || {}) };
  delete p.workspace_id;

  if (path === "/events") return eventList(p);
  if (path === "/events/uncategorized") return uncategorized(p);
  if (/^\/events\/[^/]+$/.test(path)) return row("events", path.split("/")[2]);
  if (path === "/calendar/heatmap") return heatmap(p);
  if (path === "/dashboard/summary") return dashboardSummary();
  if (path === "/matters") return (await rows("matters")).sort((a, b) => (a.title || "").localeCompare(b.title || ""));
  if (/^\/matters\/[^/]+\/diary$/.test(path)) return matterDiary(path.split("/")[2]);
  if (/^\/matters\/[^/]+$/.test(path)) return row("matters", path.split("/")[2]);
  if (path === "/courts") return rows("courts");
  if (path === "/todos") return todoList(p);
  if (path === "/todos/summary") return todoSummary(p);
  if (path === "/todos/carry-over") return carryOver(p);
  if (/^\/todos\/[^/]+$/.test(path)) return row("todos", path.split("/")[2]);
  if (path === "/postits") return postItList(p);
  if (/^\/postits\/[^/]+$/.test(path) && path !== "/postits/meta") {
    return row("postits", path.split("/")[2]);
  }
  if (path === "/logs") return logList(p);
  if (path === "/logs/review-queue") {
    const cached = await cachedRaw(config);
    if (cached) return cached;
    const pending = (await rows("logs")).filter((l) => l.review_pending);
    return { count: pending.length, logs: pending };
  }
  if (/^\/logs\/[^/]+$/.test(path)) return row("logs", path.split("/")[2]);
  if (path === "/kit/day") {
    const day = p.date || todayIso();
    const kits = (await rows("kits"))
      .filter((k) => k.date === day)
      .sort((a, b) => (a.start_time || "99:99").localeCompare(b.start_time || "99:99"));
    return { date: day, count: kits.length, kits };
  }
  if (path === "/kit/range") {
    const kits = (await rows("kits"))
      .filter((k) => (!p.start || k.date >= p.start) && (!p.end || k.date <= p.end))
      .sort(
        (a, b) =>
          a.date.localeCompare(b.date) ||
          (a.start_time || "99:99").localeCompare(b.start_time || "99:99"),
      );
    return { start: p.start, end: p.end, count: kits.length, kits };
  }
  if (/^\/kit\/[^/]+$/.test(path) && path !== "/kit/range") return row("kits", path.split("/")[2]);
  if (path === "/search") return localSearch(p.q);

  return cachedRaw(config);
};
